import React, { useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import Pagination from 'components/Pagination';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import ReactLoader from 'components/Loader';
import {
  icon_column_style,
  individual_column_style,
  individual_icon_header_style,
  individual_header_style
} from '../../../assets/styles/table_custom_style';
import useRingGroups from 'hooks/useRingGroups';
import useCostCenters from 'hooks/useCostCenters';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import { useForm } from 'react-hook-form';
import useExtensions from 'hooks/useExtensions';
import UncontrolledDefaultModal from 'components/Modal/UncontrolledDefaultModal';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const RingGroupList = () => {
  const {
    response: ringGroups,
    isLoading: isLoadingRingGroups,
    totalRegisters,
    activePage,
    searchText,
    showDisabled,
    handlePageChange,
    handleSearchChange,
    handleShowDisabledChange,
    toggleDisable,
    updateRingGroup
  } = useRingGroups();

  const {
    control,
    errors,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty }
  } = useForm();

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const { response: costCenters, isLoading: isLoadingCostCenters } =
    useCostCenters();

  const { options: extensionOptions, isLoading: isLoadingExtensionOptions } =
    useExtensions({ showAll: true });

  const findOptions = useCallback((values, options) => {
    if (!values || values.length === 0) {
      return [];
    }

    return values
      .map((value) => {
        const searchValue = String(value);
        return options.find((opt) => String(opt.value) === searchValue) || null;
      })
      .filter(Boolean);
  }, []);

  useEffect(() => {
    const resetEntries = ringGroups.map((rg) => {
      const selectedOption = findOptions(
        rg.extensions.map((ext) => ext.extension_number),
        extensionOptions
      );

      return [`ringgroup-${rg.id}`, selectedOption];
    });

    reset(Object.fromEntries(resetEntries));
  }, [ringGroups, extensionOptions, findOptions]);

  const costCentersMap = useMemo(() => {
    return costCenters.reduce((map, cc) => {
      map[cc.id] = cc.name;
      return map;
    }, {});
  }, [costCenters]);

  const getStrategyName = useCallback((strategy) => {
    const strategyMap = {
      RINGALL: 'Todos os Ramais',
      ROUNDROBIN: 'Rotativo',
      LEASTRECENT: 'Menos Recente',
      FEWESTCALLS: 'Menos Chamadas',
      RANDOM: 'Aleatório',
      RRMEMORY: 'Rotativo com Memória'
    };
    return strategyMap[strategy] || strategy;
  }, []);

  const onSubmit = useCallback(
    (data, id) => {
      if (!verifyRoles(['RING_GROUPS_ADD_EXTENSIONS']))
        toast.error(
          `Você não tem permissão para alterar os ramais de um grupo de toque`
        );

      if (!id)
        return toast.error(
          'Não foi possível encontrar o grupo de toque para atualizar'
        );

      const originalRingGroupData = ringGroups.find((val) => val.id === id);
      const dataToUpdate = data?.[`ringgroup-${id}`]?.map?.((op) => op.value);
      const updatedRingGroupData = {
        ...originalRingGroupData,
        extensionsid: Array.isArray(dataToUpdate) ? dataToUpdate : [],
        is_updating_extensions_only: true
      };

      updateRingGroup(id, updatedRingGroupData);
    },
    [ringGroups]
  );

  const columns = useMemo(() => {
    return [
      {
        dataField: 'name',
        text: 'Nome do Grupo de Toque',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'sector.name',
        text: 'Setor do Grupo de Toque',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style,
        formatter: (cell, row) => {
          return costCentersMap[row.cost_center_id] || '-';
        }
      },
      {
        dataField: 'ring_strategy',
        text: 'Estratégia do Grupo de Toque',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style,
        formatter: (cell) => getStrategyName(cell?.toUpperCase?.()) ?? '-'
      },
      {
        dataField: 'extensionsid',
        text: 'Ramais conectados',
        style: individual_column_style,
        headerStyle: individual_header_style,
        formatter: (cell, row) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RingGroupExtensionsModal
              row={row}
              extensionOptions={extensionOptions}
              control={control}
              errors={errors}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              systemConfiguration={systemConfiguration}
            />
          </div>
        )
      },
      {
        dataField: 'is_personalized',
        text: 'Grupo de Toque Personalizado',
        style: individual_column_style,
        headerStyle: individual_header_style,
        formatter: (cell) => (cell ? 'Sim' : 'Não')
      },
      {
        dataField: 'enabled',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Habilitar / Desabilitar',
        formatter: (_, row) => {
          return (
            <ToggleSwitch
              checked={row.enabled}
              id={row.name}
              onChange={() => toggleDisable(row.id, row.enabled)}
            />
          );
        }
      },
      ,
      {
        dataField: 'edit',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Editar',
        formatter: (rowContent, row) => {
          return (
            <Link to={'/admin/ringgroups/form/' + row.id}>
              <Button
                color="info"
                size="sm"
                {...(!verifyRoles(['RING_GROUPS_EDIT'])
                  ? {
                      title:
                        'Você não tem permissão para editar um grupo de toque',
                      disabled: true
                    }
                  : {})}
              >
                <li className="fas fa-user-edit"></li>
              </Button>
            </Link>
          );
        }
      }
    ];
  }, [
    extensionOptions,
    costCentersMap,
    getStrategyName,
    toggleDisable,
    onSubmit
  ]);

  return (
    <>
      <SimpleHeader
        setCreate="/admin/ringgroups/form"
        name="Lista de Grupos de Toque"
        parentName="Telefonia"
      />
      <Container className="mt--6" fluid>
        <Row className="mt-3 mx-2">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Lista de Grupos de Toque</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="8">
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Nome do Grupo de Toque"
                          type="text"
                          value={searchText}
                          onChange={(e) => handleSearchChange(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md="4" className="text-right mt-2">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={showDisabled}
                            onChange={() =>
                              handleShowDisabledChange(!showDisabled)
                            }
                          />
                          <span>Mostrar desabilitados</span>
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
                {isLoadingRingGroups ||
                isLoadingCostCenters ||
                isLoadingExtensionOptions ? (
                  <Row className="justify-content-md-center">
                    <ReactLoader />
                  </Row>
                ) : (
                  <ToolkitProvider
                    data={ringGroups}
                    keyField="id"
                    columns={columns}
                  >
                    {(props) => (
                      <div className="py-4 table-responsive">
                        {isLoadingRingGroups || isLoadingCostCenters ? (
                          <Row className="justify-content-md-center">
                            <ReactLoader />
                          </Row>
                        ) : (
                          <BootstrapTable
                            {...props.baseProps}
                            bootstrap4={true}
                            bordered={false}
                            classes="align-items-center"
                          />
                        )}
                      </div>
                    )}
                  </ToolkitProvider>
                )}
                <Pagination
                  activePage={activePage}
                  totalItemsCount={totalRegisters}
                  onChange={handlePageChange}
                />
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default RingGroupList;

const RingGroupExtensionsModal = ({
  row,
  extensionOptions,
  control,
  errors,
  handleSubmit,
  onSubmit,
  systemConfiguration
}) => {
  return (
    <UncontrolledDefaultModal
      trigger={
        <Button
          color="info"
          size="sm"
          id={`ringgroup-popover-button-${row.id}`}
          {...(!verifyRoles(['RING_GROUPS_ADD_EXTENSIONS'])
            ? {
                title:
                  'Você não tem permissão para alterar os ramais de um grupo de toque',
                disabled: true
              }
            : {})}
        >
          <li className="fas fa-headset"></li>
        </Button>
      }
      title={`Ramais conectados ao grupo de toque ${row.name}`}
      subtitle={`Configure abaixo quais ramais têm acesso a esse grupo de toque`}
      body={
        <Row style={{ paddingInline: '1rem' }}>
          <Col md="12">
            <form onSubmit={handleSubmit((data) => onSubmit(data, row.id))}>
              <Row className="align-items-center">
                <Col md="9">
                  <FlexControllerV2
                    type="select"
                    name={`ringgroup-${row.id}`}
                    control={control}
                    options={extensionOptions}
                    isMulti
                    closeMenuOnSelect={false}
                    errors={errors}
                    defaultValue={row.extensionsid}
                  />
                </Col>
                <Col md="3">
                  <Button
                    color="primary"
                    type="submit"
                    style={{ marginLeft: 'auto' }}
                  >
                    Salvar
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      }
      footer={({ closeModal }) => (
        <div className="modal-footer" style={{ paddingTop: '.5rem' }}>
          <Button
            type="button"
            color="danger"
            style={{
              backgroundColor: systemConfiguration.cancelation_button_color
            }}
            onClick={closeModal}
          >
            Fechar
          </Button>
        </div>
      )}
    />
  );
};
